import { motion } from 'framer-motion';
import { BarChart3, Play, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonChart } from '@/components/ui/Skeleton';
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart';
import { PivotTable } from '@/components/charts/PivotTable';
import { fadeInUp } from '@/design/animations';

interface FormulaField {
  id: string;
  name: string;
  formula: string;
  events: string[];
}

interface ResultsPanelProps {
  chartType: 'ts' | 'pivot';
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: unknown;
  hasRun: boolean;
  canRun: boolean;
  onRun: () => void;
  /** Multiple active formula field definitions */
  formulaFields?: FormulaField[];
  /** Supplementary data per-field (keyed by field ID) for per-event filters */
  formulaDataMap?: Record<string, Record<string, unknown>>;
  selectedEvents?: string[];
  /** Comparison period data (same shape as main data, different date range) */
  comparisonData?: Record<string, unknown>;
  /** Whether cumulative mode is on */
  cumulative?: boolean;
}

/**
 * Results panel — displays charts/tables after running a query.
 */
export function ProductResultsPanel({
  chartType,
  isLoading,
  isError,
  error,
  data,
  hasRun,
  canRun,
  onRun,
  formulaFields,
  formulaDataMap,
  selectedEvents,
  comparisonData,
  cumulative,
}: ResultsPanelProps) {
  // Loading state
  if (isLoading) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <SkeletonChart />
      </motion.div>
    );
  }

  // Error state
  if (isError) {
    return (
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card className="border-red-200/80">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-0.5">
                Query Error
              </h3>
              <p className="text-sm text-slate-500">
                {(error as Error)?.message || 'An unexpected error occurred while running the query.'}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }

  // Not yet run
  if (!hasRun) {
    return (
      <Card>
        <EmptyState
          icon={BarChart3}
          title="Ready to analyze"
          description={
            canRun
              ? 'Click "Run" to execute the query and see results'
              : 'Select a table and at least one event or custom field to get started'
          }
          action={
            canRun && (
              <Button icon={<Play />} onClick={onRun}>
                Run Query
              </Button>
            )
          }
        />
      </Card>
    );
  }

  // Results
  const resultData = data as Record<string, unknown> | undefined;

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible">
      <Card padding="none">
        <div className="p-5">
          {chartType === 'ts' ? (
            <TimeSeriesChart
              data={resultData}
              formulaFields={formulaFields}
              formulaDataMap={formulaDataMap}
              selectedEvents={selectedEvents}
              comparisonData={comparisonData}
              cumulative={cumulative}
            />
          ) : (
            <PivotTable
              data={resultData}
              formulaFields={formulaFields}
              formulaDataMap={formulaDataMap}
              selectedEvents={selectedEvents}
              comparisonData={comparisonData}
            />
          )}
        </div>
      </Card>
    </motion.div>
  );
}
